import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import {
  CheckCircle2, Clock, Circle, FileText, Download, ShieldCheck,
  AlertTriangle, Target, BookOpen, Loader2, Eye, Pencil, Save, History, X,
  RotateCcw, Activity, LogIn, Shield, FileSignature
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/compliance`;

const authHeader = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Status key: done | progress | planned
const STATUS_META = {
  done: { icon: CheckCircle2, labelKey: 'admin.iso.statusDone', color: 'text-emerald-600', bg: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  progress: { icon: Clock, labelKey: 'admin.iso.statusProgress', color: 'text-amber-600', bg: 'bg-amber-100 text-amber-800 border-amber-200' },
  planned: { icon: Circle, labelKey: 'admin.iso.statusPlanned', color: 'text-slate-500', bg: 'bg-slate-100 text-slate-700 border-slate-200' },
};

// ISO 27001:2022 Annex A Controls — Bewertung Materialbuddy
// `doc` (optional) = Dateiname in /app/compliance der per Klick geöffnet wird
const CONTROLS = [
  // A.5 Organizational Controls
  { ref: 'A.5.1', title: 'Richtlinien für Informationssicherheit', status: 'done', doc: '02_Information_Security_Policy.md', note: 'ISMS-Policy-Template vorhanden. Vom Management freizugeben.' },
  { ref: 'A.5.2', title: 'Rollen & Verantwortlichkeiten', status: 'done', note: 'Template vorhanden. CISO/DPO-Rollen müssen benannt werden.' },
  { ref: 'A.5.7', title: 'Threat Intelligence', status: 'planned', note: 'Externe Feed-Integration (CVE/Mitre) steht aus.' },
  { ref: 'A.5.8', title: 'Informationssicherheit im Projektmanagement', status: 'done', doc: '14_Security_Review_Template.md', note: 'Security-Review-Template (ISMS-14) veröffentlicht und für alle PRs/Releases verpflichtend.' },
  { ref: 'A.5.15', title: 'Zugriffssteuerung', status: 'done', doc: '06_Access_Control_Policy.md', note: 'RBAC (site-admin / buyer / seller / admin), Site-Permissions aktiv.' },
  { ref: 'A.5.17', title: 'Authentifizierungsinformationen', status: 'done', note: 'Password-Policy (10+ Zeichen, 3/4-Komplexität), Bcrypt-Hashing.' },
  { ref: 'A.5.18', title: 'Zugriffsrechte (Provisioning)', status: 'done', doc: '06_Access_Control_Policy.md', note: 'User-Einladungsflow + automatisches Deprovisioning bei Subscription-End.' },
  { ref: 'A.5.23', title: 'Cloud-Services-Sicherheit', status: 'progress', doc: '09_Supplier_Security_Register.md', note: 'MongoDB Atlas + Render. Cloud-Security-Agreement steht aus.' },
  { ref: 'A.5.24', title: 'Incident Management Planung', status: 'done', doc: '07_Incident_Response_Plan.md', note: 'Incident Response Plan Template im Repository.' },
  { ref: 'A.5.28', title: 'Beweissicherung', status: 'done', doc: '12_Audit_Log_Procedure.md', note: 'Compliance-Audit-Log mit Immutability-Design.' },
  { ref: 'A.5.30', title: 'Business Continuity / ICT Readiness', status: 'progress', doc: '10_Business_Continuity_Plan.md', note: 'BCP v1.2 inkl. DR-Test-Protokoll-Vorlage. Erster DR-Test Q1 2026 geplant.' },
  { ref: 'A.5.34', title: 'Datenschutz (PII)', status: 'progress', doc: '11_Data_Protection_Policy.md', note: 'DSGVO-Basics vorhanden; vollständiges Verfahrensverzeichnis steht aus.' },

  // A.6 People Controls
  { ref: 'A.6.3', title: 'Awareness / Schulung', status: 'planned', note: 'Mitarbeiter-Security-Training steht aus.' },
  { ref: 'A.6.6', title: 'Vertraulichkeitsvereinbarungen (NDA)', status: 'progress', note: 'NDA-Template vorhanden, noch nicht mit allen Dienstleistern unterzeichnet.' },
  { ref: 'A.6.8', title: 'Meldung von Sicherheitsvorfällen', status: 'done', doc: '07_Incident_Response_Plan.md', note: 'Bug-Reporting-System aktiv; Security-Reports über gleiches Interface möglich.' },

  // A.7 Physical Controls
  { ref: 'A.7.4', title: 'Physische Sicherheit von Rechenzentren', status: 'done', note: 'Delegiert an MongoDB Atlas (ISO 27001 zertifiziert) + Render (SOC 2 Typ II).' },
  { ref: 'A.7.10', title: 'Speichermedien', status: 'done', note: 'Managed Cloud — keine eigenen physischen Medien.' },

  // A.8 Technological Controls
  { ref: 'A.8.2', title: 'Privilegierte Zugriffsrechte', status: 'done', doc: '06_Access_Control_Policy.md', note: 'Admin-Rolle separat; Site-Admin-Scope begrenzt.' },
  { ref: 'A.8.3', title: 'Informations-Zugriffsbegrenzung', status: 'done', note: 'API-Endpoints Permission-gated; _id aus DB-Responses ausgeschlossen.' },
  { ref: 'A.8.5', title: 'Sichere Authentifizierung (MFA)', status: 'done', note: '2FA via TOTP (pyotp) + 8 Backup-Codes.' },
  { ref: 'A.8.7', title: 'Schutz vor Malware', status: 'progress', note: 'File-Upload-Validation aktiv; ClamAV-Scan im Backlog.' },
  { ref: 'A.8.8', title: 'Verwundbarkeits-Management', status: 'done', doc: '15_Vulnerability_Management.md', note: 'Policy (ISMS-15) + CI-Gates: pip-audit & yarn audit --level high blockieren Builds; Dependabot (wöchentlich) öffnet PRs für veraltete Deps; CodeQL scannt Python+JS.' },
  { ref: 'A.8.9', title: 'Konfigurations-Management', status: 'done', doc: '08_Change_Management_Policy.md', note: 'Env-Variablen zentral; keine Hardcodings.' },
  { ref: 'A.8.12', title: 'Data Leakage Prevention', status: 'progress', doc: '16_Data_Leakage_Prevention.md', note: 'DLP-Policy (ISMS-16) definiert Kanäle, Kontrollen und Monitoring. DLP-Alerting operativ auszubauen.' },
  { ref: 'A.8.13', title: 'Backup', status: 'done', doc: '10_Business_Continuity_Plan.md', note: 'mongodb_backup.sh — mongodump, 14-Tage-Retention, Integrity-Check.' },
  { ref: 'A.8.15', title: 'Protokollierung (Logging)', status: 'done', doc: '12_Audit_Log_Procedure.md', note: 'compliance_audit_log Collection, 18 Event-Typen, Failed-Login-Tracking.' },
  { ref: 'A.8.16', title: 'Überwachungsaktivitäten', status: 'progress', note: 'Admin-Dashboard zeigt Events; kein SIEM-Alerting bei Anomalien.' },
  { ref: 'A.8.21', title: 'Sicherheit der Netzdienste', status: 'done', note: 'Security-Headers-Middleware: HSTS, CSP, X-Frame, Referrer-Policy.' },
  { ref: 'A.8.23', title: 'Web-Filter', status: 'done', note: 'CSP begrenzt Script-Quellen.' },
  { ref: 'A.8.24', title: 'Kryptographie-Einsatz', status: 'done', note: 'TLS (Hoster), bcrypt, HMAC-SHA256 für Audit-Chains.' },
  { ref: 'A.8.25', title: 'Sicherer Entwicklungs-Lifecycle', status: 'done', doc: '17_Secure_Development_Lifecycle.md', note: 'SSDLC-Policy (ISMS-17) umfasst Plan→Design→Develop→Build→Test→Release→Operate inkl. Threat-Modeling, Secure-Coding-Guidelines und Umgebungstrennung.' },
  { ref: 'A.8.28', title: 'Sicherer Code', status: 'done', doc: '17_Secure_Development_Lifecycle.md', note: 'SSDLC-Policy + CI-Gates: ruff & eslint blockieren Lints; CodeQL (Python+JS) läuft wöchentlich & bei PRs; gitleaks scannt Secrets.' },
  { ref: 'A.8.29', title: 'Sicherheitstests', status: 'done', note: 'Backend-Pytests laufen als CI-Gate (fail-fast) gegen einen MongoDB-Service-Container; jede PR wird blockiert, wenn Security-Hardening-Tests fehlschlagen.' },
  { ref: 'A.8.32', title: 'Change Management', status: 'done', doc: '08_Change_Management_Policy.md', note: 'Alle Änderungen als Git-Commits in GitHub versioniert und unveränderlich gespeichert. Deployment via Render-Auto-Deploy (render.yaml Blueprint), jeder Deploy ist einem Git-SHA zugeordnet. Rollback jederzeit möglich (Render → Deploys → Rollback, < 2 Min) oder via git revert.' },
];

// Roadmap items carry a stable `key` so admins can toggle their status live.
// Baseline `defaultStatus` stays in code; overrides are persisted in
// `compliance_status_overrides` (scope="roadmap") and merged at render time.
const ROADMAP = [
  {
    phase: 'Q1 2026',
    title: 'Governance & Dokumentation formalisieren',
    items: [
      { key: 'q1-security-review-template', label: 'Security-Review-Template (ISMS-14) verabschiedet', defaultStatus: 'done' },
      { key: 'q1-vuln-mgmt-policy',         label: 'Vulnerability-Management-Policy (ISMS-15) verabschiedet', defaultStatus: 'done' },
      { key: 'q1-dlp-policy',                label: 'Data-Leakage-Prevention-Policy (ISMS-16) verabschiedet', defaultStatus: 'done' },
      { key: 'q1-ssdlc-policy',              label: 'SSDLC-Policy (ISMS-17) verabschiedet', defaultStatus: 'done' },
      { key: 'q1-bcp-v12',                   label: 'BCP v1.2 inkl. DR-Test-Protokoll', defaultStatus: 'done' },
      { key: 'q1-mgmt-approval',             label: 'Management-Approval für ISMS-Policy (Geschäftsführung)', defaultStatus: 'planned' },
      { key: 'q1-ciso-dpo',                  label: 'CISO / DPO formal benennen', defaultStatus: 'planned' },
      { key: 'q1-risk-register-top10',       label: 'Risiko-Register mit Top-10-Risiken füllen', defaultStatus: 'done' },
      { key: 'q1-soa-final',                 label: 'Statement of Applicability (SoA) finalisieren', defaultStatus: 'progress' },
    ],
  },
  {
    phase: 'Q2 2026',
    title: 'Operational Security Controls',
    items: [
      { key: 'q2-dependabot-audit',  label: 'Dependabot + pip-audit/yarn-audit in CI-Pipeline integrieren', defaultStatus: 'done' },
      { key: 'q2-sast-codeql',        label: 'SAST-Integration (GitHub CodeQL + Bandit + Semgrep)', defaultStatus: 'progress' },
      { key: 'q2-dr-test-first',      label: 'Erster Disaster-Recovery-Test nach neuem Protokoll (RTO/RPO validieren)', defaultStatus: 'planned' },
      { key: 'q2-dlp-alerting',       label: 'DLP-Alerting (Massen-Exports, Secret-Scan-Webhooks)', defaultStatus: 'progress' },
      { key: 'q2-awareness-training', label: 'Mitarbeiter-Awareness-Training (Phishing-Simulation)', defaultStatus: 'planned' },
    ],
  },
  {
    phase: 'Q3 2026',
    title: 'Audit & Zertifizierung',
    items: [
      { key: 'q3-internal-audit',    label: 'Internes Audit durch akkreditierten Auditor', defaultStatus: 'planned' },
      { key: 'q3-mgmt-review',       label: 'Management-Review-Meeting', defaultStatus: 'planned' },
      { key: 'q3-stage1-audit',      label: 'Stage-1 Audit (Dokumentationsprüfung)', defaultStatus: 'planned' },
      { key: 'q3-stage2-audit',      label: 'Stage-2 Audit (Vor-Ort-Prüfung) → Zertifikat', defaultStatus: 'planned' },
    ],
  },
  {
    phase: 'Q4 2026',
    title: 'Continuous Improvement',
    items: [
      { key: 'q4-kpi-dashboard', label: 'KPI-Dashboard: MTTR, Failed-Logins-Rate, Patch-SLA', defaultStatus: 'planned' },
      { key: 'q4-siem-alerting', label: 'SIEM-Alerting (z. B. Datadog / Wazuh)', defaultStatus: 'planned' },
      { key: 'q4-pentest',        label: 'Externer Pentest (Web-App + API)', defaultStatus: 'planned' },
      { key: 'q4-dr-test-second', label: 'Zweiter DR-Test + Lessons-Learned', defaultStatus: 'planned' },
      { key: 'q4-soc2-prep',      label: 'Ausbau: SOC 2 Type II vorbereiten', defaultStatus: 'planned' },
    ],
  },
];

const StatusPill = ({ status }) => {
  const { t } = useTranslation();
  const meta = STATUS_META[status];
  const Icon = meta.icon;
  return (
    <Badge variant="outline" className={`${meta.bg} font-medium gap-1`}>
      <Icon className="h-3 w-3" />{t(meta.labelKey)}
    </Badge>
  );
};

const prettyTitle = (filename) =>
  filename.replace(/\.md$/i, '').replace(/^\d+_/, '').replace(/_/g, ' ');

export const ISO27001ReportTab = () => {
  const { t } = useTranslation();
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  // Persisted status overrides: { roadmap: {key: status}, control: {ref: status} }
  const [overrides, setOverrides] = useState({ roadmap: {}, control: {} });
  const [kpis, setKpis] = useState(null);
  const [kpisLoading, setKpisLoading] = useState(true);
  const [pulseExporting, setPulseExporting] = useState(false);
  const [savingKey, setSavingKey] = useState(null); // e.g. "roadmap:q2-dependabot-audit"
  const [viewer, setViewer] = useState({
    open: false, filename: null, title: '', content: '', loading: false,
    mode: 'view', draft: '', saving: false, modifiedAt: null,
  });
  const [revisions, setRevisions] = useState({ open: false, loading: false, items: [] });

  // Effective status per control (baseline → override)
  const effectiveControls = useMemo(
    () => CONTROLS.map(c => ({ ...c, status: overrides.control[c.ref] || c.status })),
    [overrides.control]
  );

  // Effective status per roadmap item
  const effectiveRoadmap = useMemo(
    () => ROADMAP.map(p => ({
      ...p,
      items: p.items.map(it => ({ ...it, status: overrides.roadmap[it.key] || it.defaultStatus })),
    })),
    [overrides.roadmap]
  );

  const stats = useMemo(() => {
    const done = effectiveControls.filter(c => c.status === 'done').length;
    const progress = effectiveControls.filter(c => c.status === 'progress').length;
    const planned = effectiveControls.filter(c => c.status === 'planned').length;
    const total = effectiveControls.length;
    const score = Math.round((done / total) * 100);
    return { done, progress, planned, total, score };
  }, [effectiveControls]);

  const roadmapStats = useMemo(() => {
    const all = effectiveRoadmap.flatMap(p => p.items);
    const done = all.filter(i => i.status === 'done').length;
    return { done, total: all.length };
  }, [effectiveRoadmap]);

  const loadDocs = async () => {
    setDocsLoading(true);
    try {
      const res = await axios.get(`${API}/docs`, { headers: authHeader() });
      setDocs(res.data.docs || []);
    } catch (e) {
      toast.error(t('admin.iso.docsLoadError'));
    } finally {
      setDocsLoading(false);
    }
  };

  const loadOverrides = async () => {
    try {
      const res = await axios.get(`${API}/status-overrides`, { headers: authHeader() });
      const map = { roadmap: {}, control: {} };
      (res.data.overrides || []).forEach(o => {
        if (o.scope === 'roadmap' || o.scope === 'control') {
          map[o.scope][o.key] = o.status;
        }
      });
      setOverrides(map);
    } catch (e) {
      // Non-fatal — fall back to defaults
    }
  };

  const loadKpis = async () => {
    setKpisLoading(true);
    try {
      const res = await axios.get(`${API}/kpis?window_days=30`, { headers: authHeader() });
      setKpis(res.data);
    } catch (e) {
      setKpis(null);
    } finally {
      setKpisLoading(false);
    }
  };

  useEffect(() => { loadDocs(); loadOverrides(); loadKpis(); }, []);

  const STATUS_CYCLE = { planned: 'progress', progress: 'done', done: 'planned' };

  const cycleStatus = async (scope, key, currentStatus) => {
    const next = STATUS_CYCLE[currentStatus] || 'planned';
    const saveKey = `${scope}:${key}`;
    setSavingKey(saveKey);
    // Optimistic update
    setOverrides(prev => ({ ...prev, [scope]: { ...prev[scope], [key]: next } }));
    try {
      await axios.put(
        `${API}/status-overrides`,
        { scope, key, status: next },
        { headers: authHeader() }
      );
      toast.success(t('admin.iso.statusUpdated'));
    } catch (e) {
      // Revert
      setOverrides(prev => {
        const copy = { ...prev, [scope]: { ...prev[scope] } };
        if (currentStatus) copy[scope][key] = currentStatus; else delete copy[scope][key];
        return copy;
      });
      toast.error(t('admin.iso.statusSaveFailed'));
    } finally {
      setSavingKey(null);
    }
  };

  const resetStatus = async (scope, key) => {
    const saveKey = `${scope}:${key}`;
    setSavingKey(saveKey);
    try {
      await axios.delete(`${API}/status-overrides`, {
        headers: authHeader(),
        params: { scope, key },
      });
      setOverrides(prev => {
        const copy = { ...prev, [scope]: { ...prev[scope] } };
        delete copy[scope][key];
        return copy;
      });
      toast.info(t('admin.iso.statusReset'));
    } catch (e) {
      toast.error(t('admin.iso.statusSaveFailed'));
    } finally {
      setSavingKey(null);
    }
  };

  const closeViewer = () => {
    setViewer({ open: false, filename: null, title: '', content: '', loading: false, mode: 'view', draft: '', saving: false, modifiedAt: null });
    setRevisions({ open: false, loading: false, items: [] });
  };

  const openDoc = async (filename) => {
    if (!filename) return;
    setViewer(v => ({ ...v, open: true, filename, title: prettyTitle(filename), content: '', loading: true, mode: 'view', draft: '', saving: false, modifiedAt: null }));
    try {
      const res = await axios.get(`${API}/docs/${encodeURIComponent(filename)}`, { headers: authHeader() });
      setViewer({
        open: true,
        filename,
        title: res.data.title || prettyTitle(filename),
        content: res.data.content || '',
        loading: false,
        mode: 'view',
        draft: res.data.content || '',
        saving: false,
        modifiedAt: res.data.modified_at || null,
      });
    } catch (e) {
      toast.error(t('admin.iso.docNotFound', { filename }));
      closeViewer();
    }
  };

  const enterEditMode = () => {
    setViewer(v => ({ ...v, mode: 'edit', draft: v.content }));
  };

  const cancelEdit = () => {
    if (viewer.draft !== viewer.content && !window.confirm(t('admin.iso.discardChanges'))) return;
    setViewer(v => ({ ...v, mode: 'view', draft: v.content }));
  };

  const saveDoc = async () => {
    if (!viewer.filename) return;
    setViewer(v => ({ ...v, saving: true }));
    try {
      const res = await axios.put(
        `${API}/docs/${encodeURIComponent(viewer.filename)}`,
        { content: viewer.draft },
        { headers: authHeader() }
      );
      if (res.data?.unchanged) {
        toast.info(t('admin.iso.noChangesToSave'));
      } else {
        toast.success(t('admin.iso.docSaved'));
      }
      setViewer(v => ({
        ...v,
        content: v.draft,
        mode: 'view',
        saving: false,
        modifiedAt: res.data?.modified_at || v.modifiedAt,
      }));
      // Refresh list to show new modified_at / size
      loadDocs();
    } catch (e) {
      const msg = e?.response?.data?.detail || t('admin.iso.saveFailed');
      toast.error(msg);
      setViewer(v => ({ ...v, saving: false }));
    }
  };

  const openRevisions = async () => {
    if (!viewer.filename) return;
    setRevisions({ open: true, loading: true, items: [] });
    try {
      const res = await axios.get(`${API}/docs/${encodeURIComponent(viewer.filename)}/revisions`, { headers: authHeader() });
      setRevisions({ open: true, loading: false, items: res.data.revisions || [] });
    } catch (e) {
      toast.error(t('admin.iso.revisionsLoadError'));
      setRevisions({ open: false, loading: false, items: [] });
    }
  };

  const restoreRevision = async (revisionId) => {
    if (!viewer.filename || !revisionId) return;
    try {
      const res = await axios.get(`${API}/docs/${encodeURIComponent(viewer.filename)}/revisions/${revisionId}`, { headers: authHeader() });
      const content = res.data?.content || '';
      setViewer(v => ({ ...v, draft: content, mode: 'edit' }));
      setRevisions({ open: false, loading: false, items: [] });
      toast.info(t('admin.iso.versionLoadedInEditor'));
    } catch (e) {
      toast.error(t('admin.iso.revisionLoadError'));
    }
  };

  const downloadCurrentDoc = () => {
    if (!viewer.content || !viewer.filename) return;
    const blob = new Blob([viewer.content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = viewer.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportMarkdown = () => {
    const date = new Date().toISOString().slice(0, 10);
    let md = `# ISO 27001 Implementation Report — Materialbuddy\n\n`;
    md += `**Stand:** ${date}\n\n`;
    md += `**Compliance-Score:** ${stats.score}% (${stats.done}/${stats.total} Controls)\n\n`;
    md += `- Umgesetzt: ${stats.done}\n- In Arbeit: ${stats.progress}\n- Geplant: ${stats.planned}\n\n`;
    md += `## Control-Status (ISO 27001:2022 Annex A)\n\n`;
    md += `| Ref | Control | Status | Hinweis |\n|-----|---------|--------|---------|\n`;
    for (const c of effectiveControls) {
      md += `| ${c.ref} | ${c.title} | ${t(STATUS_META[c.status].labelKey)} | ${c.note} |\n`;
    }
    md += `\n## Roadmap (${roadmapStats.done}/${roadmapStats.total} erledigt)\n\n`;
    for (const p of effectiveRoadmap) {
      md += `### ${p.phase} — ${p.title}\n`;
      for (const it of p.items) {
        const mark = it.status === 'done' ? '✅' : it.status === 'progress' ? '🟡' : '⬜';
        md += `- ${mark} ${it.label}\n`;
      }
      md += `\n`;
    }
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `iso27001-report-${date}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPulse = async () => {
    if (pulseExporting) return;
    setPulseExporting(true);
    try {
      const payload = {
        controls: effectiveControls.map(c => ({
          ref: c.ref,
          title: c.title,
          status: c.status,
          note: c.note || '',
        })),
        roadmap: effectiveRoadmap.map(p => ({
          phase: p.phase,
          title: p.title,
          items: p.items.map(it => ({ label: it.label, status: it.status })),
        })),
        window_days: 30,
      };
      const res = await axios.post(
        `${API}/pulse-export`,
        payload,
        {
          headers: { ...authHeader(), 'Content-Type': 'application/json' },
          responseType: 'blob',
        }
      );
      const url = URL.createObjectURL(
        new Blob([res.data], { type: 'application/pdf' })
      );
      const a = document.createElement('a');
      a.href = url;
      const date = new Date().toISOString().slice(0, 10);
      a.download = `compliance-pulse-${date}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      const hash = res.headers?.['x-compliance-pulse-hash'];
      toast.success(
        hash
          ? t('admin.iso.pulseExportedWithHash', { hash: hash.slice(0, 12) })
          : t('admin.iso.pulseExported')
      );
    } catch (e) {
      toast.error(t('admin.iso.pulseExportFailed'));
    } finally {
      setPulseExporting(false);
    }
  };

  const scoreColor = stats.score >= 70 ? 'text-emerald-600' : stats.score >= 50 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="space-y-6" data-testid="iso27001-report-tab">
      {/* Header with overall score */}
      <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="iso-header-card">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-10 w-10 text-emerald-600" />
              <div>
                <div className="text-xl font-bold tracking-tight">{t('admin.iso.reportTitle')}</div>
                <div className="text-sm text-muted-foreground">{t('admin.iso.dateAnnex', { date: new Date().toLocaleDateString(), total: stats.total })}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={exportPulse}
                disabled={pulseExporting}
                data-testid="iso-pulse-export-btn"
                className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                title={t('admin.iso.pulseExportTooltip')}
              >
                {pulseExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileSignature className="h-4 w-4" />
                )}
                {t('admin.iso.pulseExport')}
              </Button>
              <Button
                onClick={exportMarkdown}
                variant="outline"
                data-testid="iso-export-btn"
                className="gap-1.5"
              >
                <Download className="h-4 w-4" />{t('admin.iso.exportReport')}
              </Button>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-baseline justify-between mb-1.5">
              <div className="text-sm text-muted-foreground">{t('admin.iso.complianceScore')}</div>
              <div className={`text-3xl font-bold ${scoreColor}`} data-testid="iso-score">{stats.score}%</div>
            </div>
            <Progress value={stats.score} className="h-2.5" />
            <div className="text-xs text-muted-foreground mt-1.5">
              {t('admin.iso.controlsDoneOf', { done: stats.done, total: stats.total })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card data-testid="iso-stat-done" className="border-emerald-200">
          <CardContent className="pt-6 flex items-center justify-between">
            <div>
              <div className="text-sm text-muted-foreground">{t('admin.iso.statusDone')}</div>
              <div className="text-3xl font-bold text-emerald-600">{stats.done}</div>
            </div>
            <CheckCircle2 className="h-8 w-8 text-emerald-500" />
          </CardContent>
        </Card>
        <Card data-testid="iso-stat-progress" className="border-amber-200">
          <CardContent className="pt-6 flex items-center justify-between">
            <div>
              <div className="text-sm text-muted-foreground">{t('admin.iso.statusProgress')}</div>
              <div className="text-3xl font-bold text-amber-600">{stats.progress}</div>
            </div>
            <Clock className="h-8 w-8 text-amber-500" />
          </CardContent>
        </Card>
        <Card data-testid="iso-stat-planned" className="border-slate-200">
          <CardContent className="pt-6 flex items-center justify-between">
            <div>
              <div className="text-sm text-muted-foreground">{t('admin.iso.statusPlanned')}</div>
              <div className="text-3xl font-bold text-slate-600">{stats.planned}</div>
            </div>
            <Circle className="h-8 w-8 text-slate-400" />
          </CardContent>
        </Card>
      </div>

      {/* Live-KPI-Dashboard (MTTR / Failed-Logins / Patch-SLA) */}
      <Card data-testid="iso-kpi-card" className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Activity className="h-5 w-5 text-emerald-600" />
            {t('admin.iso.kpiTitle')}
            <Badge variant="outline" className="ml-2 bg-slate-100 text-slate-700 font-normal">
              {t('admin.iso.kpiWindow', { days: 30 })}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {kpisLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('admin.common.loading')}
            </div>
          ) : !kpis ? (
            <div className="text-sm text-muted-foreground py-4">
              {t('admin.iso.kpiLoadError')}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* MTTR */}
              <div
                className="rounded-lg border p-4 bg-gradient-to-br from-emerald-50/40 to-white"
                data-testid="kpi-mttr"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t('admin.iso.kpiMttr')}
                  </div>
                  <Clock className="h-4 w-4 text-emerald-600" />
                </div>
                <div className="text-3xl font-bold text-slate-900">
                  {kpis.mttr.avg_hours != null
                    ? `${kpis.mttr.avg_hours} h`
                    : '—'}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {t('admin.iso.kpiMttrSample', { n: kpis.mttr.sample_size })} · {t('admin.iso.kpiTarget')}: ≤ {kpis.mttr.target_hours} h
                </div>
                {kpis.mttr.avg_hours != null && (
                  <Badge
                    variant="outline"
                    className={`mt-2 ${kpis.mttr.avg_hours <= kpis.mttr.target_hours ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-amber-100 text-amber-800 border-amber-200'}`}
                  >
                    {kpis.mttr.avg_hours <= kpis.mttr.target_hours ? t('admin.iso.kpiOnTarget') : t('admin.iso.kpiOverTarget')}
                  </Badge>
                )}
              </div>

              {/* Failed-Login-Rate */}
              <div
                className="rounded-lg border p-4 bg-gradient-to-br from-red-50/40 to-white"
                data-testid="kpi-failed-login"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t('admin.iso.kpiFailedLogin')}
                  </div>
                  <LogIn className="h-4 w-4 text-red-500" />
                </div>
                <div className="text-3xl font-bold text-slate-900">
                  {kpis.failed_login_rate.per_1000_attempts} ‰
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {kpis.failed_login_rate.failed} / {kpis.failed_login_rate.failed + kpis.failed_login_rate.success} · {t('admin.iso.kpiTarget')}: ≤ {kpis.failed_login_rate.target_per_1000} ‰
                </div>
                <Badge
                  variant="outline"
                  className={`mt-2 ${kpis.failed_login_rate.per_1000_attempts <= kpis.failed_login_rate.target_per_1000 ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-red-100 text-red-800 border-red-200'}`}
                >
                  {kpis.failed_login_rate.per_1000_attempts <= kpis.failed_login_rate.target_per_1000 ? t('admin.iso.kpiOnTarget') : t('admin.iso.kpiOverTarget')}
                </Badge>
              </div>

              {/* Patch-SLA */}
              <div
                className="rounded-lg border p-4 bg-gradient-to-br from-blue-50/40 to-white"
                data-testid="kpi-patch-sla"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t('admin.iso.kpiPatchSla')}
                  </div>
                  <Shield className="h-4 w-4 text-blue-600" />
                </div>
                <div className="text-3xl font-bold text-slate-900">
                  {kpis.patch_sla.within_sla_pct != null
                    ? `${kpis.patch_sla.within_sla_pct}%`
                    : t('admin.iso.kpiNoData')}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {t('admin.iso.kpiPatchSample', { within: kpis.patch_sla.within_sla, total: kpis.patch_sla.sample_size })} · {t('admin.iso.kpiTarget')}: ≥ {kpis.patch_sla.target_pct}%
                </div>
                {kpis.patch_sla.within_sla_pct != null && (
                  <Badge
                    variant="outline"
                    className={`mt-2 ${kpis.patch_sla.within_sla_pct >= kpis.patch_sla.target_pct ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-amber-100 text-amber-800 border-amber-200'}`}
                  >
                    {kpis.patch_sla.within_sla_pct >= kpis.patch_sla.target_pct ? t('admin.iso.kpiOnTarget') : t('admin.iso.kpiOverTarget')}
                  </Badge>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Controls table */}
      <Card data-testid="iso-controls-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="h-5 w-5" />{t('admin.iso.controlsStatusTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-2 pr-3 font-medium w-20">{t('admin.iso.tableRef')}</th>
                  <th className="py-2 pr-3 font-medium">{t('admin.iso.tableControl')}</th>
                  <th className="py-2 pr-3 font-medium w-32">{t('admin.iso.tableStatus')}</th>
                  <th className="py-2 pr-3 font-medium">{t('admin.iso.tableNote')}</th>
                  <th className="py-2 pr-3 font-medium w-28">{t('admin.iso.tableDoc')}</th>
                </tr>
              </thead>
              <tbody data-testid="iso-controls-rows">
                {effectiveControls.map((c) => {
                  const isOverridden = Boolean(overrides.control[c.ref]);
                  const savingThis = savingKey === `control:${c.ref}`;
                  return (
                    <tr key={c.ref} className="border-b last:border-0 hover:bg-muted/30 align-top">
                      <td className="py-2 pr-3 font-mono text-xs font-semibold text-slate-700">{c.ref}</td>
                      <td className="py-2 pr-3 font-medium">{c.title}</td>
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => cycleStatus('control', c.ref, c.status)}
                            disabled={savingThis}
                            title={t('admin.iso.clickToToggleStatus')}
                            data-testid={`iso-control-status-${c.ref}`}
                            className="cursor-pointer hover:scale-105 transition-transform disabled:opacity-50"
                          >
                            <StatusPill status={c.status} />
                          </button>
                          {isOverridden && (
                            <button
                              type="button"
                              onClick={() => resetStatus('control', c.ref)}
                              disabled={savingThis}
                              title={t('admin.iso.resetToDefault')}
                              data-testid={`iso-control-reset-${c.ref}`}
                              className="text-muted-foreground hover:text-slate-800"
                            >
                              <RotateCcw className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-muted-foreground text-xs leading-relaxed">{c.note}</td>
                      <td className="py-2 pr-3">
                        {c.doc ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs gap-1"
                            onClick={() => openDoc(c.doc)}
                            data-testid={`iso-control-doc-${c.ref}`}
                            title={`${c.doc} ${t('admin.iso.view')}`}
                          >
                            <Eye className="h-3 w-3" />{t('admin.iso.view')}
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Roadmap */}
      <Card data-testid="iso-roadmap-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Target className="h-5 w-5" />{t('admin.iso.roadmapTitle')}
            <Badge variant="outline" className="ml-2 bg-slate-100 text-slate-700 font-normal">
              {t('admin.iso.roadmapDoneOf', { done: roadmapStats.done, total: roadmapStats.total })}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-xs text-muted-foreground mb-3">
            {t('admin.iso.roadmapInteractiveHint')}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {effectiveRoadmap.map((p) => (
              <div key={p.phase} className="border rounded-lg p-4 bg-slate-50/50" data-testid={`iso-roadmap-${p.phase.replace(/\s/g, '-')}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-blue-100 text-blue-800 border-blue-200">{p.phase}</Badge>
                  <div className="font-semibold text-sm">{p.title}</div>
                </div>
                <ul className="space-y-2 text-sm">
                  {p.items.map((it) => {
                    const meta = STATUS_META[it.status];
                    const Icon = meta.icon;
                    const isOverridden = Boolean(overrides.roadmap[it.key]);
                    const savingThis = savingKey === `roadmap:${it.key}`;
                    const strike = it.status === 'done';
                    return (
                      <li key={it.key} className="flex items-start gap-2 group">
                        <button
                          type="button"
                          onClick={() => cycleStatus('roadmap', it.key, it.status)}
                          disabled={savingThis}
                          title={t('admin.iso.clickToToggleStatus')}
                          data-testid={`iso-roadmap-toggle-${it.key}`}
                          className={`mt-0.5 shrink-0 rounded-full p-0.5 hover:bg-white hover:shadow-sm transition disabled:opacity-50 ${meta.color}`}
                        >
                          {savingThis
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <Icon className="h-4 w-4" />}
                        </button>
                        <span className={`flex-1 ${strike ? 'text-muted-foreground line-through decoration-emerald-400/60' : 'text-slate-700'}`}>
                          {it.label}
                        </span>
                        {isOverridden && (
                          <button
                            type="button"
                            onClick={() => resetStatus('roadmap', it.key)}
                            disabled={savingThis}
                            title={t('admin.iso.resetToDefault')}
                            data-testid={`iso-roadmap-reset-${it.key}`}
                            className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-slate-800 transition"
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Policy Documents (live from /app/compliance) */}
      <Card data-testid="iso-docs-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <BookOpen className="h-5 w-5" />{t('admin.iso.policiesTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground mb-3">
            {docsLoading
              ? t('admin.iso.loadDocsList')
              : t('admin.iso.docsInRepo', { count: docs.length })}
            {!docsLoading && <code className="px-1.5 py-0.5 bg-muted rounded text-xs">/app/compliance/</code>}
            {!docsLoading && t('admin.iso.clickToView')}
          </div>
          {docsLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />{t('admin.common.loading')}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="iso-docs-list">
              {docs.map((d) => (
                <button
                  key={d.filename}
                  onClick={() => openDoc(d.filename)}
                  className="flex items-center justify-between gap-2 text-sm p-2.5 bg-muted/40 rounded hover:bg-muted transition text-left group"
                  data-testid={`iso-doc-${d.filename}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-4 w-4 text-slate-500 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium truncate">{prettyTitle(d.filename)}</div>
                      <code className="text-[10px] text-muted-foreground truncate block">{d.filename}</code>
                    </div>
                  </div>
                  <Eye className="h-4 w-4 text-slate-400 group-hover:text-primary shrink-0" />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Disclaimer */}
      <Alert className="border-amber-200 bg-amber-50" data-testid="iso-disclaimer">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-sm text-amber-900">
          <strong>{t('admin.common.note')}:</strong> {t('admin.iso.reportDisclaimer')}
        </AlertDescription>
      </Alert>

      {/* Document Viewer Dialog */}
      <Dialog open={viewer.open} onOpenChange={(o) => !o && closeViewer()}>
        <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col" data-testid="iso-doc-viewer">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />{viewer.title}
              {viewer.mode === 'edit' && (
                <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200">{t('admin.iso.editMode')}</Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              <code className="text-xs">{viewer.filename}</code> · {t('admin.iso.from')} <code className="text-xs">/app/compliance/</code>
              {viewer.modifiedAt && (
                <> · {t('admin.iso.lastModified')}: {new Date(viewer.modifiedAt).toLocaleString()}</>
              )}
            </DialogDescription>
          </DialogHeader>

          {viewer.loading ? (
            <div className="flex items-center justify-center py-10 gap-2 text-muted-foreground flex-1">
              <Loader2 className="h-4 w-4 animate-spin" />{t('admin.iso.loadingDoc')}
            </div>
          ) : viewer.mode === 'view' ? (
            <div className="flex-1 overflow-y-auto border rounded-md p-4 bg-white" data-testid="iso-doc-viewer-content">
              <article className="prose prose-sm max-w-none prose-headings:mt-4 prose-headings:mb-2 prose-h1:text-xl prose-h2:text-lg prose-h3:text-base prose-p:my-2 prose-li:my-0.5 prose-table:text-xs">
                <ReactMarkdown>{viewer.content || t('admin.iso.noContent')}</ReactMarkdown>
              </article>
            </div>
          ) : (
            <Tabs defaultValue="split" className="flex-1 flex flex-col min-h-0">
              <TabsList className="w-fit" data-testid="iso-doc-edit-tabs">
                <TabsTrigger value="split" data-testid="iso-doc-tab-split">{t('admin.iso.tabSplit')}</TabsTrigger>
                <TabsTrigger value="editor" data-testid="iso-doc-tab-editor">{t('admin.iso.tabEditor')}</TabsTrigger>
                <TabsTrigger value="preview" data-testid="iso-doc-tab-preview">{t('admin.iso.tabPreview')}</TabsTrigger>
              </TabsList>

              <TabsContent value="split" className="flex-1 overflow-hidden mt-2">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 h-full min-h-0">
                  <Textarea
                    value={viewer.draft}
                    onChange={(e) => setViewer(v => ({ ...v, draft: e.target.value }))}
                    className="font-mono text-xs h-full min-h-[400px] resize-none"
                    data-testid="iso-doc-editor-split"
                    spellCheck={false}
                  />
                  <div className="border rounded-md p-4 bg-white overflow-y-auto min-h-[400px]">
                    <article className="prose prose-sm max-w-none prose-headings:mt-4 prose-headings:mb-2 prose-h1:text-xl prose-h2:text-lg prose-h3:text-base">
                      <ReactMarkdown>{viewer.draft || t('admin.iso.noContent')}</ReactMarkdown>
                    </article>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="editor" className="flex-1 overflow-hidden mt-2">
                <Textarea
                  value={viewer.draft}
                  onChange={(e) => setViewer(v => ({ ...v, draft: e.target.value }))}
                  className="font-mono text-xs h-full min-h-[400px] resize-none"
                  data-testid="iso-doc-editor-full"
                  spellCheck={false}
                />
              </TabsContent>

              <TabsContent value="preview" className="flex-1 overflow-y-auto mt-2 border rounded-md p-4 bg-white">
                <article className="prose prose-sm max-w-none prose-headings:mt-4 prose-headings:mb-2 prose-h1:text-xl prose-h2:text-lg prose-h3:text-base">
                  <ReactMarkdown>{viewer.draft || t('admin.iso.noContent')}</ReactMarkdown>
                </article>
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter className="flex-wrap gap-2 sm:gap-2">
            {viewer.mode === 'view' ? (
              <>
                <Button variant="outline" onClick={openRevisions} disabled={viewer.loading} data-testid="iso-doc-revisions-btn" className="gap-1.5">
                  <History className="h-4 w-4" />{t('admin.iso.versions')}
                </Button>
                <Button variant="outline" onClick={downloadCurrentDoc} disabled={viewer.loading || !viewer.content} data-testid="iso-doc-download-btn" className="gap-1.5">
                  <Download className="h-4 w-4" />{t('admin.iso.downloadMd')}
                </Button>
                <Button onClick={enterEditMode} disabled={viewer.loading} data-testid="iso-doc-edit-btn" className="gap-1.5">
                  <Pencil className="h-4 w-4" />{t('admin.iso.edit')}
                </Button>
                <Button variant="secondary" onClick={closeViewer} data-testid="iso-doc-close-btn">
                  {t('admin.iso.close')}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={cancelEdit} disabled={viewer.saving} data-testid="iso-doc-cancel-edit-btn" className="gap-1.5">
                  <X className="h-4 w-4" />{t('admin.iso.cancel')}
                </Button>
                <Button
                  onClick={saveDoc}
                  disabled={viewer.saving || viewer.draft === viewer.content}
                  data-testid="iso-doc-save-btn"
                  className="gap-1.5"
                >
                  {viewer.saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {t('common.save')}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revisions Dialog */}
      <Dialog open={revisions.open} onOpenChange={(o) => !o && setRevisions({ open: false, loading: false, items: [] })}>
        <DialogContent className="max-w-2xl" data-testid="iso-doc-revisions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />{t('admin.iso.versionHistory')}
            </DialogTitle>
            <DialogDescription>
              {t('admin.iso.earlierStatesOf')} <code className="text-xs">{viewer.filename}</code>. {t('admin.iso.loadVersionHint')}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto">
            {revisions.loading ? (
              <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />{t('admin.iso.loadVersions')}
              </div>
            ) : revisions.items.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-8">
                {t('admin.iso.noPrevVersions')}
              </div>
            ) : (
              <div className="space-y-2" data-testid="iso-doc-revisions-list">
                {revisions.items.map((r) => (
                  <div key={r.revision_id} className="flex items-center justify-between gap-3 p-3 bg-muted/40 rounded border">
                    <div className="text-xs min-w-0">
                      <div className="font-medium">{new Date(r.saved_at).toLocaleString()}</div>
                      <div className="text-muted-foreground truncate">
                        {t('admin.iso.replacedBy')}: {r.replaced_by_email || r.replaced_by_user_id} · {r.size_bytes} B · <code className="text-[10px]">{r.content_hash?.slice(0, 8)}</code>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => restoreRevision(r.revision_id)}
                      data-testid={`iso-doc-restore-${r.revision_id}`}
                      className="gap-1.5 shrink-0"
                    >
                      <Eye className="h-3 w-3" />{t('admin.iso.load')}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRevisions({ open: false, loading: false, items: [] })} data-testid="iso-doc-revisions-close-btn">
              {t('admin.iso.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ISO27001ReportTab;
